import { useEffect, useRef, useState } from "react";
import { saveProgress } from "../storage/progress";
import { findLanguageIndex, isOriginalLanguage, languageLabel } from "../lib/languages";
import { describeCast, startCast, watchCast } from "../playback/cast";
import { loadRelaySubtitles } from "../playback/pi-relay";

// Player for admin-mapped torrents and hosted files (LuluStream/Playmate HLS).
//
// Playback modes:
//   • HLS (torrent remux + hosted direct streams): hls.js on desktop/Android,
//     native HLS on Safari/iPhone. Language chips switch audio renditions.
//   • Direct stream: the raw file over HTTP Range, used until the remux is
//     ready — or forever for single-audio torrents.
//
// UI is PoKu's own control bar (same icon language as the embed players):
// play/pause, ±10s, scrubber, time, volume, fullscreen — fading after 3s of
// no movement, with keyboard shortcuts (space, arrows, f, m). Progress is
// saved with the same store the Vidking embed feeds via postMessage.

let hlsLoaderPromise = null;
function loadHls() {
  if (!hlsLoaderPromise) {
    hlsLoaderPromise = import("hls.js").then((m) => m.default);
  }
  return hlsLoaderPromise;
}

// When a hosted <video> fails, MEDIA_ERR tells you almost nothing: a removed
// file, a CDN 500 and a request a blocker cancelled on this machine all surface
// as the same error code on the element. One tiny 2-byte range GET on the very
// same URL separates the two families, because they answer differently:
//
//   • the host answers (206, or even 404) — the file is reachable, so
//     something in the browser refused to play it: uBlock Origin, Brave
//     Shields, a VPN/proxy content filter, or a battery/power saver.
//   • the fetch REJECTS — either a blocker stopped the request before it left
//     the machine (the usual case, and it surfaces as net::ERR_BLOCKED_BY_CLIENT
//     in the network log), or the response came back without an
//     Access-Control-Allow-Origin header, which is what a CDN error page looks
//     like to a cross-origin fetch. That second case is how an EXPIRED playback
//     token presents, so it is called out rather than blamed on an extension.
//
// Logged, not shown: this is a diagnostic for the console, and the overlay keeps
// its own short human-readable copy.
async function diagnoseMediaFailure(url, code) {
  const short = String(url || "").slice(0, 90);
  console.warn(`[poku] media element failed (code ${code ?? "?"}) on ${short}`);
  if (!/^https?:/i.test(url || "")) {
    console.warn("[poku] not an absolute URL — nothing to probe (this build may be same-origin)");
    return;
  }
  try {
    const res = await fetch(url, { headers: { range: "bytes=0-1" }, cache: "no-store" });
    console.warn(
      `[poku] the host answered HTTP ${res.status} for a 2-byte range — the file IS ` +
        `reachable, so playback was refused locally (extension, Brave Shields, or proxy).`,
    );
  } catch (err) {
    console.warn(
      `[poku] could not read the media URL (${err?.message || err}). Either a blocker ` +
        `stopped it (check for net::ERR_BLOCKED_BY_CLIENT in the network log), or the ` +
        `response carried no Access-Control-Allow-Origin — which is how an expired ` +
        `StreamTape token looks, since its error page is not CORS-enabled.`,
    );
  }
}

const fmtSecs = (s) => {
  const sec = Math.round(s);
  return sec >= 0 ? `+${sec}s` : `${sec}s`;
};

const fmtTime = (s) => {
  if (!Number.isFinite(s) || s <= 0) return "0:00";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const mm = h ? String(m).padStart(2, "0") : String(m);
  const ss = String(sec).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

const Icon = {
  play: "M8 5v14l11-7z",
  pause: "M6 19h4V5H6v14zm8-14v14h4V5h-4z",
  replay: "M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z",
  forward:
    "M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z",
  volUp:
    "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z",
  volMute:
    "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z",
  fsEnter: "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z",
  // Material "cast" — the TV-with-waves glyph next to fullscreen.
  cast: "M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z",
  fsExit: "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z",
  // Material "closed caption" — the CC toggle in the bar. Drawn as the letters
  // CC inside a screen, so the control reads as closed-captions at a glance.
  subs: "M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z",
  // Material "settings" — the gear that opens the audio-track sheet.
  settings:
    "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
};

function BarButton({ label, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      className={`torrent-btn ${className}`.trim()}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
    >
      {children}
    </button>
  );
}

export default function TorrentVideo({ source, request, onBack, onStreamDead }) {
  // Admin-assigned audio language for hosted links ("Tamil", "Telugu"…) —
  // shown as a badge so users know which language this link carries.
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const hlsRef = useRef(null);
  const lastSavedRef = useRef({ position: -10, at: 0 });
  // Hybrid: the host supplies the video, we supply the audio renditions (and the
  // subtitles, fetched below like every other native source). `audioTracks`
  // arrives harvested from our own HLS master, so the language switcher already
  // knows every language the package holds. Derived first because the HLS state
  // below is seeded from it.
  const hybrid = Boolean(source.kind === "hybrid" && source.videoUrl);
  const hybridTracks = Array.isArray(source.audioTracks) ? source.audioTracks : [];
  // Two shapes share the hybrid kind, and the difference decides who owns the
  // sound. With package audio, the host's flattened track is muted for the whole
  // session and our rendition carries it. WITHOUT package audio (multi mode's
  // subtitles-only build: the second source had one track, so there was nothing
  // to choose) the host's own track IS the sound — muting it would mean silence.
  const hybridAudio = hybrid && hybridTracks.length > 0;
  // Which rendition starts playing. The film's ORIGINAL language wins over both
  // the host's DEFAULT=YES flag and the admin's note: a Malayalam film whose
  // package lists Tamil first must open in Malayalam. `source.audio` (the
  // admin's "this link carries Malayalam") stays the fallback for the rare title
  // TMDB gives us no language for.
  const hybridDefaultIndex = (() => {
    const byLanguage = findLanguageIndex(hybridTracks, request.originalLanguage);
    if (byLanguage >= 0) return byLanguage;
    const byAdmin = hybridTracks.findIndex(
      (t) => String(t.label || "").toLowerCase() === String(source.audio || "").toLowerCase()
    );
    return Math.max(0, byAdmin);
  })();
  const [attempt, setAttempt] = useState(0);
  // Torrent/TorBox sources poll a status URL; hosted HLS has nothing to poll,
  // so it starts clean and the overlay is driven purely by video events
  // (onWaiting/onPlaying) — no more spinner that can hang forever.
  const [swarm, setSwarm] = useState({
    loading: Boolean(source.statusUrl),
    progress: 0,
    peers: 0,
    speed: 0,
    error: null,
  });
  // Hosted-file sources (LuluStream/Playmate direct HLS) have no swarm/status
  // poll — they start in HLS mode immediately.
  const audioRef = useRef(null);
  const audioHlsRef = useRef(null);
  const [hybridIndex, setHybridIndex] = useState(hybridDefaultIndex);
  const [hls, setHls] = useState({
    active: Boolean(source.hlsUrl && !source.statusUrl),
    tracks: hybrid ? hybridTracks : [],
    current: hybrid ? hybridDefaultIndex : -1,
  });
  const [controls, setControls] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(true); // matches the muted autoplay start
  const [fullscreen, setFullscreen] = useState(false);
  // Casting (Chromecast / AirPlay) — see playback/cast.js. The button appears
  // only when the browser has a native picker AND the element is playing a URL
  // the TV can fetch itself; an hls.js MediaSource or the loopback torrent
  // stream is not castable and gets no button at all.
  const [cast, setCast] = useState({ route: null, castable: false, connected: false, available: true });
  const [ripple, setRipple] = useState(null); // { side: "left"|"right"|"center", label }
  const [activeAt, setActiveAt] = useState(() => Date.now());
  const lastTapRef = useRef({ t: 0, x: 0, y: 0, timer: null });
  const rippleTimerRef = useRef(null);
  // Revoke function for blob: URLs created by loadRelaySubtitles. Called on
  // unmount or whenever a new subtitle load replaces them.
  const relaySubsRevokeRef = useRef(null);
  // Admin-mapped subtitles (SRT/VTT served from PoKu as WebVTT <track>s).
  const [subs, setSubs] = useState([]);
  // In-manifest subtitles. A LuluStream/Playmate master declares its own
  // SUBTITLES renditions (Thudakkam ships English, DEFAULT=YES) and hls.js
  // publishes them as text tracks IT owns — not <track> children, so the CC
  // button, which only counted admin-mapped tracks, never appeared on a hosted
  // movie that plainly had subtitles. They get their own switch: hls.subtitleTrack.
  const [hlsSubs, setHlsSubs] = useState([]);
  const [hlsCc, setHlsCc] = useState(null); // in-manifest track index; null = off
  const hlsCcRef = useRef(null);
  const hlsCcTouchedRef = useRef(false);
  const [ccOn, setCcOn] = useState(null); // null = off; else admin track label
  // The settings sheet (audio tracks + subtitle language). Two bar controls
  // lead here differently: the CC button toggles subtitles directly, the gear
  // opens the sheet. The chips that used to sit over the top-right corner of
  // the picture covered the movie and added a row of buttons per language.
  const [menuOpen, setMenuOpen] = useState(false);
  // Did the user pick a subtitle track on purpose? Subtitles start ON, but an
  // explicit "CC off" must never be undone by a later track list arriving.
  const ccTouchedRef = useRef(false);
  // Same guard for the two switches the viewer can move by hand: audio
  // language and in-manifest subtitles.
  const hybridTouchedRef = useRef(false);
  // Mirrors `hybridAudio` for the one-shot gesture handler, which is installed
  // once on mount and must read the CURRENT mode, not the mount-time one. A
  // host-audio hybrid follows the ordinary path: the <video> is unmuted.
  const hybridAudioRef = useRef(false);

  // Gesture-tap zones: <30% width = back, >70% = forward, middle = play/pause.
  const GESTURE_EDGE = 0.3;

  // Autoplay starts muted (browser rule), but the first user gesture restores
  // full volume — a movie should never sit at zero volume until the user
  // hunts for the slider.
  // Hybrid sources are the exception: the <video> is the HOST's stream (its own
  // flattened audio track), so it stays muted for the whole session — the
  // gesture unmutes our separate audio rendition instead, via state.
  useEffect(() => {
    let done = false;
    const unmute = () => {
      if (done) return;
      done = true;
      if (hybridAudioRef.current) {
        setMuted(false);
        setVolume(1);
        return;
      }
      const v = videoRef.current;
      if (v && (v.muted || v.volume === 0)) {
        v.muted = false;
        v.volume = 1;
        setMuted(false);
        setVolume(1);
      }
    };
    const opts = { passive: true };
    window.addEventListener("pointerdown", unmute, opts);
    window.addEventListener("keydown", unmute, opts);
    window.addEventListener("touchstart", unmute, opts);
    return () => {
      window.removeEventListener("pointerdown", unmute, opts);
      window.removeEventListener("keydown", unmute, opts);
      window.removeEventListener("touchstart", unmute, opts);
    };
  }, []);

  const statusUrl = source.statusUrl;
  const streamUrl = source.url;
  const hlsUrl = source.hlsUrl || null;
  hybridAudioRef.current = hybridAudio;
  // A HOSTED file is any source with no torrent status endpoint behind it —
  // both the torrent and torbox providers always set statusUrl, while every
  // hosted shape (direct HLS, hybrid, and StreamTape's progressive mp4) omits
  // it. Testing hlsUrl/hybrid instead misread the mp4 as a torrent: it got the
  // "no seeds right now" copy for a file that was simply unreachable, and
  // because that branch also gates onStreamDead, the chain never fell through
  // to the next provider — a hard dead end on a title that plays fine.
  const isHosted = !statusUrl;

  // Fetch the admin's subtitle tracks for this title (native-playback sources
  // only — the video never carries usable subs after host transcoding).
  useEffect(() => {
    const type = request.contentType === "EPISODE" ? "episode" : "movie";
    const qs = new URLSearchParams({ type, id: String(request.tmdbId) });
    if (request.contentType === "EPISODE" && request.season != null && request.episode != null) {
      qs.set("season", String(request.season));
      qs.set("episode", String(request.episode));
    }
    // Relay base captured once at the start of this effect run.
    const relayBase = source.relayBase || null;
    let stop = false;
    // Revoke any blob: URLs from the previous relay subtitle load.
    if (relaySubsRevokeRef.current) {
      relaySubsRevokeRef.current();
      relaySubsRevokeRef.current = null;
    }
    const applyTracks = (list) => {
      if (stop) return;
      setSubs(list);
      // Subtitles are ON by default (English preferred), matching the
      // injected embed bar — a movie should never start with none. A track
      // the user already chose is left alone.
      setCcOn((cur) => {
        if (ccTouchedRef.current || cur != null || !list.length) return cur;
        const english = list.find((t) => /english|^en\b/i.test(`${t.label || ""} ${t.lang || ""}`));
        return (english || list[0]).label;
      });
    };
    // Last resort: the Pi relay's own subtitle store. Reached when the public
    // store has no track AND autofetch produces none — or FAILS outright, which
    // it does in production today: config.js points apiBase at the Netlify
    // origin, so native-api.js rewrites /api/* to it, and a POST with a JSON
    // content-type is not a simple request. Its preflight OPTIONS 404s there,
    // so the fetch rejects. Keeping this out of the autofetch promise chain is
    // the point — nested inside it, a rejection skipped the relay entirely and
    // the outer catch swallowed it, leaving no tracks and so no CC button.
    const tryRelay = () => {
      if (!relayBase) {
        applyTracks([]);
        return undefined;
      }
      return loadRelaySubtitles(relayBase, request)
        .then(({ tracks: relayTracks, revoke }) => {
          if (stop) {
            revoke();
            return;
          }
          relaySubsRevokeRef.current = revoke;
          applyTracks(relayTracks);
        })
        .catch(() => {
          applyTracks([]);
        });
    };
    fetch(`/api/subs/get?${qs.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d.tracks) ? d.tracks : [];
        if (list.length) {
          applyTracks(list);
          return undefined;
        }
        // Nothing mapped to this title. Subtitles belong to the MOVIE, not to
        // the file we happen to hold — one release ships an ESub inside its
        // 250 MB rip while the 4 GB WEB-DL has none at all. The server matches
        // by TMDB id (SubDL, then Wyzie by IMDb id), so ask it to find one
        // instead of leaving the title subless. The injected embed bar already
        // does this for proxied sources; this is the same call, so a
        // self-hosted package heals exactly like a vidking embed.
        return fetch("/api/subs/autofetch", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            type,
            id: request.tmdbId,
            season: request.season,
            episode: request.episode,
          }),
        })
          .then((r) => r.json())
          .then((d2) => {
            const list2 = Array.isArray(d2.tracks) ? d2.tracks : [];
            if (list2.length) {
              applyTracks(list2);
              return undefined;
            }
            return tryRelay();
          })
          // Autofetch rejected (CORS preflight, offline, 5xx). Still try the
          // relay: its store is independent of whether autofetch can run.
          .catch(() => tryRelay());
      })
      // The public-store lookup itself failed. tryRelay() never rejects, so
      // this cannot double-apply.
      .catch(() => tryRelay());
    return () => {
      stop = true;
      // revoke() is idempotent, so the start-of-effect call above and this one
      // cannot double-free; this is the unmount path.
      if (relaySubsRevokeRef.current) {
        relaySubsRevokeRef.current();
        relaySubsRevokeRef.current = null;
      }
    };
  }, [request.contentType, request.tmdbId, request.season, request.episode, source.relayBase]);

  // Poll the server's swarm status while the torrent is not fully downloaded.
  useEffect(() => {
    if (!statusUrl) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(statusUrl);
        const d = await r.json();
        if (stop) return;
        if (statusUrl.includes("/api/torbox/")) {
          // TorBox probe shape: { state, progress, cached, error }
          setSwarm((s) => ({
            ...s,
            loading: d.state === "queued" || (!d.cached && d.state !== "ready" && !d.error),
            progress: d.progress || (d.state === "ready" ? 1 : 0),
            peers: d.cached ? -1 : 0, // -1 = "cached, instant"
            speed: 0,
            error: d.state === "error" ? d.error || "TorBox error" : null,
          }));
          return;
        }
        setSwarm((s) => ({
          ...s,
          loading: !d.mapped ? s.loading : !(d.ready || d.progress >= 0.02 || d.downloaded > 500_000),
          progress: d.progress || 0,
          peers: d.numPeers || 0,
          speed: d.downloadSpeed || 0,
        }));
        // Adopt HLS once the remux lands (only if we are not already on it).
        if (d.hls?.status === "ready" && (d.hls.tracks || []).length > 0) {
          setHls((h) =>
            h.active
              ? h
              : { active: true, tracks: d.hls.tracks, current: d.hls.defaultIndex ?? 0 },
          );
        }
      } catch {
        /* status is best-effort */
      }
    };
    tick();
    const iv = setInterval(tick, 2500);
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, [statusUrl]);

  // (Re)build the player whenever the mode, chosen language or retry changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const resumeAt = Number(request.resumePositionSeconds || 0);
    let disposed = false;
    setTime(0);
    setDur(0);
    // A new source carries its own subtitle renditions; the previous movie's
    // list must not leak into it (and a fresh "CC on" default is correct for a
    // new file — the viewer's earlier tap belonged to the old one).
    setHlsSubs([]);
    setHlsCc(null);
    hlsCcRef.current = null;
    hlsCcTouchedRef.current = false;

    const applyResume = () => {
      if (resumeAt >= 5 && Math.abs(v.currentTime - resumeAt) > 5) {
        try {
          v.currentTime = resumeAt;
        } catch {
          /* seek before metadata — ignore */
        }
      }
    };

    if (hybrid) {
      // Hybrid: video from the host. When our package carries the audio, the
      // host's own track is muted for the whole session (the gesture-unmute
      // below skips it too), because the sound the user hears comes from our
      // rendition in the audio element. Without package audio it stays audible.
      v.muted = hybridAudio;
      v.src = source.videoUrl;
      const onDead = () => {
        setSwarm((s) => ({
          ...s,
          loading: false,
          error: "The host's video could not be opened — trying another source.",
        }));
        // Hand it up so the chain falls through to the next link/provider
        // instead of parking the user here: the audio alone is unwatchable.
        if (onStreamDead) onStreamDead();
      };
      let watchdog = setTimeout(() => {
        if (!disposed && !v.readyState) onDead();
      }, 20000);
      v.addEventListener("error", onDead);
      v.addEventListener("loadedmetadata", () => {
        applyResume();
        clearTimeout(watchdog);
      }, { once: true });
      return () => {
        disposed = true;
        clearTimeout(watchdog);
        v.removeEventListener("error", onDead);
        v.removeAttribute("src");
      };
    }

    if (hls.active && hlsUrl) {
      // HLS mode. hls.js first everywhere it works (Chrome, Firefox and desktop
      // Safari) — its error reporting beats Safari's native HLS, which fails
      // silently (black screen, no events). But "first" is not "only": an
      // engine that can read the playlist itself now takes over on a fatal
      // error OR a stall (see retryNative / WATCHDOG_MS), and for engines with
      // no MSE at all (iPhone/iPad) it is the only path there has ever been.
      //
      // The raw playlist is the way to tell the two failures apart: if
      // <package>/master.m3u8 plays when pasted into the address bar, the
      // package is fine and the fault is in here, not in the file.
      const nativeHlsError = () => {
        if (v.error) {
          setSwarm((s) => ({
            ...s,
            loading: false,
            error: "The stream could not be opened. The host may have removed the file or it is temporarily down.",
          }));
          // Hosted sources: hand the failure up so the player can fall
          // through to the next provider instead of a dead-end error.
          if (isHosted && onStreamDead) onStreamDead();
        }
      };
      let watchdog = null;
      let nativeRetry = false;
      // An engine that can play HLS itself gets a SHORTER leash: 20s of black is
      // only defensible when nothing else can take over, and here something can.
      const NATIVE_CAPABLE = Boolean(v.canPlayType("application/vnd.apple.mpegurl"));
      const WATCHDOG_MS = NATIVE_CAPABLE ? 6000 : 20000;
      const armWatchdog = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => {
          if (!disposed && !v.readyState) {
            if (retryNative()) return;
            setSwarm((s) => ({
              ...s,
              loading: false,
              error: "The stream is not loading. The host may be down — try again in a moment.",
            }));
            // Hosted sources: 20s with zero data is a dead link for today —
            // fall through to the next provider.
            if (isHosted && onStreamDead) onStreamDead();
          }
        }, WATCHDOG_MS);
      };
      v.addEventListener("error", nativeHlsError);
      const startNative = () => {
        v.src = hlsUrl;
        v.addEventListener("loadedmetadata", () => {
          applyResume();
          clearTimeout(watchdog);
        }, { once: true });
        armWatchdog();
        return () => {
          clearTimeout(watchdog);
          v.removeEventListener("error", nativeHlsError);
          v.removeAttribute("src");
        };
      };

      // One-way fallback to the engine's own player: stop and detach hls.js
      // first (its destroy() clears the element), then hand it the same url.
      // nativeRetry makes it a single attempt, and canPlayType keeps Chromium —
      // which answers "" for a playlist — out of this branch entirely.
      const retryNative = () => {
        if (nativeRetry || disposed) return false;
        if (!v.canPlayType("application/vnd.apple.mpegurl")) return false;
        nativeRetry = true;
        const previous = cleanup;
        cleanup = null;
        if (previous) previous();
        cleanup = startNative();
        return true;
      };

      const attach = (Hls) => {
        if (disposed) return () => {};
        // Safari 17+ (macOS and iOS alike) exposes ManagedMediaSource, and
        // hls.js 1.7 PREFERS it by default — getMediaSource() returns it
        // whenever the engine has one. That is the whole reason one title
        // played in Brave while both Safari builds sat on a silent black
        // screen: no Chromium or Firefox ships ManagedMediaSource, so Brave
        // stayed on the classic MediaSource path. The package was never at
        // fault — the playlist declares avc1.640028 / mp4a.40.2, the bytes are
        // High 4.0 H.264 and AAC-LC 48 kHz stereo, CORS `*` on every asset, and
        // Safari's own player reads the same master.m3u8 from the address bar.
        // Opting out restores the MediaSource path hls.js has always used on
        // desktop Safari; on Chromium the setting is inert.
        const hlsInstance = new Hls({ enableWorker: true, lowLatencyMode: false, backBufferLength: 90, preferManagedMediaSource: false });
        hlsRef.current = hlsInstance;
        // Alternate audio is published AFTER the manifest event: hls.js emits
        // AUDIO_TRACKS_UPDATED once the audio renditions are resolved, and
        // reading hls.audioTracks at MANIFEST_PARSED returns an EMPTY list. That
        // timing — not the playlist — is why a direct (remotely hosted) HLS
        // never showed the language switcher even though it declared three.
        //
        // The playlist is also the only source of truth for a direct HLS: a
        // remotely hosted library has no status endpoint to poll. Where the
        // torrent status path already supplied tracks, its names are richer
        // (admin-renamed in the panel), so keep those when the counts agree.
        // `t.lang` is the manifest's LANGUAGE tag and `t.name` its NAME — which
        // on a LuluStream master is the language written in its own script
        // ("തമിഴ്"…). Both are resolved to one English label here, so the sheet,
        // the Original tag and the matching below all agree on one spelling.
        const syncAudioTracks = () => {
          const list = (hlsInstance.audioTracks || []).map((t, i) => ({
            index: i,
            name: languageLabel(t.lang, t.name || t.lang),
            code: t.lang || null,
          }));
          if (!list.length) return;
          // The film's own language, NOT EXT-X-MEDIA DEFAULT=YES: a dubbing
          // house sets that on whichever track it authored first (Thudakkam
          // declares Tamil default for a Malayalam film).
          const wanted = findLanguageIndex(list, request.originalLanguage);
          setHls((s) => {
            const adopted = s.tracks.length > 0 && s.tracks.length === list.length;
            return {
              ...s,
              tracks: adopted ? s.tracks : list,
              // Never derive an index from an empty list — that set audioTrack
              // to -1.
              current: wanted >= 0 ? wanted : adopted ? s.current : hlsInstance.audioTrack ?? 0,
            };
          });
          if (wanted >= 0 && hlsInstance.audioTrack !== wanted) {
            try {
              hlsInstance.audioTrack = wanted;
            } catch {
              /* level not settled yet — the language effect below retries */
            }
          }
        };
        // The manifest's own subtitle renditions. hls.js renders them itself
        // (renderTextTracksNatively), so the CC button drives them through
        // hls.subtitleTrack; we only mirror the list for the bar.
        const syncSubtitleTracks = (data) => {
          const source = Array.isArray(data?.subtitleTracks)
            ? data.subtitleTracks
            : hlsInstance.subtitleTracks || [];
          const list = source.map((t, i) => ({
            index: i,
            name: languageLabel(t.lang, t.name),
            code: t.lang || null,
            isDefault: Boolean(t.default),
          }));
          if (!list.length) return;
          setHlsSubs(list);
          // Subtitles start ON, English preferred — the same rule the
          // admin-mapped tracks follow. An explicit "CC off" is never undone.
          if (hlsCcTouchedRef.current || hlsCcRef.current != null) return;
          const pick =
            list.find((t) => t.isDefault) ||
            list.find((t) => /english|^en\b/i.test(`${t.name} ${t.code || ""}`)) ||
            list[0];
          setHlsCc(pick.index);
        };
        hlsInstance.on(Hls.Events.AUDIO_TRACKS_UPDATED, syncAudioTracks);
        hlsInstance.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_e, data) => syncSubtitleTracks(data));
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          syncAudioTracks();
          syncSubtitleTracks();
          applyResume();
          clearTimeout(watchdog);
        });
        hlsInstance.on(Hls.Events.ERROR, (_e, data) => {
          if (data?.fatal) {
            // Before parking the viewer on an error screen, hand the SAME url to
            // the engine itself — the audio and Live TV paths already trust
            // Safari's native player, and it reads this package fine.
            if (retryNative()) return;
            setSwarm((s) => ({
              ...s,
              loading: false,
              error: "The stream could not be opened. The host may have removed the file or it is temporarily down.",
            }));
            // Hosted sources: network/manifest failures mean the link is
            // unusable right now (CDN block, dead file) — fall through to the
            // next provider rather than parking the user on an error screen.
            const networkKind = data.type === Hls.ErrorTypes.NETWORK_ERROR || data.details === "manifestLoadError" || data.details === "manifestParsingError";
            if (isHosted && onStreamDead && networkKind) onStreamDead();
          }
        });
        hlsInstance.loadSource(hlsUrl);
        hlsInstance.attachMedia(v);
        armWatchdog();
        return () => {
          clearTimeout(watchdog);
          if (hlsRef.current) hlsRef.current.destroy();
          hlsRef.current = null;
        };
      };

      let cleanup = null;
      const guard = (fn) => {
        cleanup = fn || null;
      };
      if (disposed) return undefined;
      loadHls()
        .then((Hls) => {
          if (disposed) return;
          if (!Hls.isSupported()) guard(startNative()); // iPhone/iPad: no MSE
          else guard(attach(Hls));
        })
        .catch(() => {
          if (!disposed) guard(startNative()); // hls.js failed to load — native fallback
        });
      return () => {
        disposed = true;
        clearTimeout(watchdog);
        v.removeEventListener("error", nativeHlsError);
        if (cleanup) cleanup();
      };
    }

    // Direct mode. A raw file over HTTP Range — the torrent's own stream until
    // the remux is ready, and forever for a StreamTape progressive mp4. It
    // needs the same two safety nets the HLS paths have: a hosted file that
    // errors, or that never delivers a single byte in 20s, hands the failure up
    // so the chain walks to the next provider instead of parking the viewer on
    // a dead "Stream unavailable" screen. The <video>'s own onError prop owns
    // the message; this only escalates.
    const onDead = () => {
      if (isHosted && onStreamDead) onStreamDead();
    };
    let watchdog = setTimeout(() => {
      if (!disposed && !v.readyState) onDead();
    }, 20000);
    v.addEventListener("error", onDead);
    v.src = streamUrl;
    v.addEventListener("loadedmetadata", () => {
      applyResume();
      clearTimeout(watchdog);
    }, { once: true });
    return () => {
      disposed = true;
      clearTimeout(watchdog);
      v.removeEventListener("error", onDead);
      v.removeAttribute("src");
    };
  }, [hls.active, hlsUrl, streamUrl, hybrid, hybridAudio, source.videoUrl, request.resumePositionSeconds, attempt]);

  // The in-manifest subtitle switch: hls.js owns those text tracks, so CC is
  // hls.subtitleTrack (-1 turns them off, the lib's own sentinel).
  useEffect(() => {
    hlsCcRef.current = hlsCc;
    const h = hlsRef.current;
    if (!h || !hlsSubs.length) return;
    h.subtitleDisplay = true;
    h.subtitleTrack = hlsCc == null ? -1 : hlsCc;
  }, [hlsCc, hlsSubs]);

  // A film's original language can arrive a beat after playback starts: the
  // /watch URL carries it, but Continue Watching has no language to pass, so
  // PlayerScreen fetches the title's metadata instead. Re-applying the pick
  // here means a late answer still moves the default audio rather than being
  // ignored by the mount-time closure above.
  useEffect(() => {
    if (!request.originalLanguage || hls.tracks.length < 2) return;
    const h = hlsRef.current;
    if (!h) return;
    const idx = findLanguageIndex(hls.tracks, request.originalLanguage);
    if (idx >= 0 && idx !== h.audioTrack) {
      h.audioTrack = idx;
      setHls((s) => ({ ...s, current: idx }));
    }
  }, [request.originalLanguage, hls.tracks, hls.active, hlsUrl]);

  // Same rule for a hybrid package: its renditions are ours, and the film's own
  // language wins over the admin's label unless the viewer chose a track.
  useEffect(() => {
    if (!hybridAudio || hybridTouchedRef.current || !request.originalLanguage) return;
    const idx = findLanguageIndex(hybridTracks, request.originalLanguage);
    if (idx < 0 || idx === hybridIndex) return;
    setHybridIndex(idx);
    setHls((s) => ({ ...s, current: idx }));
    // hybridTracks is derived per render; the count is the identity that matters.
  }, [hybridAudio, hybridTracks.length, request.originalLanguage, hybridIndex]);

  // Hybrid audio: our rendition, loaded beside the host's video and held on the
  // video's clock. Two independent streams is the whole point (the host flattens
  // audio to one track and drops subtitles), so playback events keep them
  // aligned and a 1s tick corrects any divergence past 300ms — measured drift on
  // the Irumudi package was 21ms of duration over 2h21m, so in practice the
  // tick rarely has anything to do.
  useEffect(() => {
    if (!hybridAudio) return undefined;
    const v = videoRef.current;
    const a = audioRef.current;
    const track = hybridTracks[hybridIndex] || hybridTracks[0];
    if (!v || !a || !track) return undefined;

    let disposedAudio = false;
    const offset = Number(source.offsetMs || 0) / 1000;
    const target = () => Math.max(0, v.currentTime + offset);
    const align = () => {
      if (!Number.isFinite(a.currentTime)) return;
      try {
        a.currentTime = target();
      } catch {
        /* not seekable yet — the tick retries */
      }
    };
    const startAudio = () => a.play().catch(() => {});
    const onPlay = () => { align(); startAudio(); };
    const onPause = () => a.pause();
    const onSeeking = () => a.pause();
    const onSeeked = () => { align(); if (!v.paused) startAudio(); };
    const onWaiting = () => a.pause(); // video buffering: hold audio so it can't run ahead
    const onPlaying = () => { if (!v.paused) startAudio(); };
    const onRate = () => { a.playbackRate = v.playbackRate; };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("ratechange", onRate);

    const tick = setInterval(() => {
      if (v.paused || disposedAudio) return;
      // Self-heal: autoplay policy, a stall, or a track swap can leave the
      // audio paused while the video runs.
      if (a.paused && a.readyState >= 2) { startAudio(); return; }
      if (a.readyState < 2) return;
      if (Math.abs(a.currentTime - target()) > 0.3) align();
    }, 1000);

    const attach = (Hls) => {
      if (disposedAudio) return () => {};
      // Same ManagedMediaSource opt-out as the video above — Safari normally
      // takes the native branch just below, but an in-app webview that answers
      // "" for canPlayType would otherwise land on the Safari-only path.
      const instance = new Hls({ enableWorker: true, lowLatencyMode: false, backBufferLength: 60, preferManagedMediaSource: false });
      audioHlsRef.current = instance;
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        align();
        if (!v.paused) startAudio();
      });
      instance.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal) {
          setSwarm((s) => ({ ...s, error: "The audio track could not be loaded for this source." }));
        }
      });
      instance.loadSource(track.url);
      instance.attachMedia(a);
      return () => {
        if (audioHlsRef.current) audioHlsRef.current.destroy();
        audioHlsRef.current = null;
      };
    };

    let cleanupAudio = () => {};
    if (a.canPlayType("application/vnd.apple.mpegurl")) {
      a.src = track.url; // iPhone/iPad native HLS
    } else {
      loadHls()
        .then((Hls) => {
          if (disposedAudio) return;
          if (Hls.isSupported()) cleanupAudio = attach(Hls);
          else a.src = track.url;
        })
        .catch(() => {
          if (!disposedAudio) a.src = track.url;
        });
    }

    return () => {
      disposedAudio = true;
      clearInterval(tick);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("ratechange", onRate);
      cleanupAudio();
      a.pause();
      a.removeAttribute("src");
    };
    // hybridTracks is derived from props on every render; the identity that
    // matters is which rendition index is selected.
  }, [hybridAudio, hybridIndex, source.offsetMs, attempt]);

  // Hybrid: the bar's volume/mute control our audio rendition, since the host's
  // video stays muted.
  useEffect(() => {
    if (!hybridAudio) return undefined;
    const a = audioRef.current;
    if (!a) return undefined;
    a.volume = volume;
    a.muted = muted;
    return undefined;
  }, [hybridAudio, volume, muted]);

  // Cast support follows the element and the browser: which native picker it
  // has, whether the current source can be handed to a TV, whether a receiver
  // is connected, and whether any device is on the network (see cast.js).
  useEffect(() => watchCast(videoRef.current, setCast), []);

  // Fullscreen state follows the browser (element fullscreen on
  // desktop/Android) or the video's native iOS playback so the icon flips.
  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  // Any movement keeps the chrome up; 3s of stillness while playing fades it.
  // An open settings sheet pins it: the sheet lives inside the bar, so letting
  // idle fade hide the bar would snatch the audio/subtitle list away mid-choice.
  useEffect(() => {
    if (!playing || menuOpen) {
      setControls(true);
      return undefined;
    }
    const t = setTimeout(() => setControls(false), 3000);
    return () => clearTimeout(t);
  }, [playing, activeAt, menuOpen]);

  useEffect(() => {
    const bump = () => setActiveAt(Date.now());
    const el = stageRef.current;
    if (el) {
      el.addEventListener("pointermove", bump);
      el.addEventListener("pointerdown", bump);
    }
    return () => {
      if (el) {
        el.removeEventListener("pointermove", bump);
        el.removeEventListener("pointerdown", bump);
      }
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const showRipple = (side, label) => {
    clearTimeout(rippleTimerRef.current);
    setRipple({ side, label });
    rippleTimerRef.current = setTimeout(() => setRipple(null), 650);
  };

  // YouTube-style tap layer, per spec: double-tap left/right seeks ∓10s with
  // a ripple, double-tap center fullscreens, center tap plays/pauses, and any
  // other single tap shows/hides the control icons.
  const handleStageTap = (e) => {
    if (e.target.closest(".torrent-ui, .player-back, .torrent-lang-panel, .player-overlay, button, input")) return;
    // A tap on the picture puts the chrome away — and the sheet with it.
    setMenuOpen(false);
    const now = Date.now();
    const rect = stageRef.current?.getBoundingClientRect();
    const relX = rect ? (e.clientX - rect.left) / rect.width : 0.5;
    const zone = relX < GESTURE_EDGE ? "left" : relX > 1 - GESTURE_EDGE ? "right" : "center";
    const last = lastTapRef.current;
    const isDouble = now - last.t < 320 && Math.abs(e.clientX - last.x) < 60 && Math.abs(e.clientY - last.y) < 60;
    clearTimeout(last.timer);
    lastTapRef.current = { t: now, x: e.clientX, y: e.clientY, timer: null };
    if (isDouble) {
      lastTapRef.current.t = 0; // consumed — a third tap starts a new sequence
      if (zone === "left") {
        skip(-10);
        showRipple("left", "-10s");
      } else if (zone === "right") {
        skip(10);
        showRipple("right", "+10s");
      } else {
        toggleFullscreen();
      }
      return;
    }
    if (zone === "center") {
      // Center tap acts immediately: play/pause.
      togglePlay();
      setActiveAt(Date.now());
    } else {
      // Single tap on the seek zones (or anywhere else): wait out the
      // double-tap window, then toggle the control icons.
      last.timer = setTimeout(() => {
        setControls((c) => !c);
        setActiveAt(Date.now());
      }, 280);
    }
  };

  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(v.duration || Number.MAX_SAFE_INTEGER, Math.max(0, v.currentTime + delta));
    setActiveAt(Date.now());
  };

  const changeVolume = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    // Hybrid: never touch the <video> — it is the host's stream and must stay
    // silent. Volume state is mirrored onto our audio element by an effect.
    const next = hybridAudio
      ? Math.min(1, Math.max(0, Math.round(((muted ? 0 : volume) + delta) * 100) / 100))
      : Math.min(1, Math.max(0, Math.round(((v.muted ? 0 : v.volume) + delta) * 100) / 100));
    if (!hybridAudio) {
      v.volume = next;
      v.muted = next === 0;
    }
    setVolume(next);
    setMuted(next === 0);
  };

  const enterFullscreen = () => {
    const v = videoRef.current;
    const el = stageRef.current;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => {
        if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone fallback
      });
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (v?.webkitEnterFullscreen) {
      // iPhone Safari: no element fullscreen — the OS-level video player is
      // the fullscreen (it covers the whole screen incl. the URL bar).
      v.webkitEnterFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else {
      const v = videoRef.current;
      if (v && v.webkitExitFullscreen) v.webkitExitFullscreen();
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) exitFullscreen();
    else enterFullscreen();
    setActiveAt(Date.now());
  };

  // Opens the TV picker. "unavailable" means no device answered and
  // "unsupported" means this element cannot hand its media over; either way the
  // state is re-read so a button that cannot lead anywhere does not stay
  // clickable. The video keeps playing here until the receiver takes over.
  const toggleCast = async () => {
    const v = videoRef.current;
    if (!v) return;
    setActiveAt(Date.now());
    const result = await startCast(v);
    setCast({
      ...describeCast(v),
      ...(result === "unavailable" ? { available: false } : {}),
    });
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    if (hybridAudio) {
      setMuted((m) => !m);
      return;
    }
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // Keyboard: ←/→ seek ±10s, ↑/↓ volume ±5, space/k play-pause, f fullscreen,
  // m mute — mirroring the embed player's shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          skip(-10);
          break;
        case "ArrowRight":
          skip(10);
          break;
        case "ArrowUp":
          e.preventDefault();
          changeVolume(0.05);
          break;
        case "ArrowDown":
          e.preventDefault();
          changeVolume(-0.05);
          break;
        case "f":
          toggleFullscreen();
          break;
        case "m":
          toggleMute();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Language switch (hls.js path — Safari uses its native audio menu).
  // Hybrid sources switch by swapping our audio rendition instead of an
  // in-manifest track; the sync effect re-aligns the new element when it lands.
  const switchLanguage = (index) => {
    if (hybridAudio) {
      // An explicit choice outranks the original-language default.
      hybridTouchedRef.current = true;
      setHybridIndex(index);
      setHls((s) => ({ ...s, current: index }));
      return;
    }
    const h = hlsRef.current;
    if (h) {
      h.audioTrack = index;
      setHls((s) => ({ ...s, current: index }));
    }
  };

  const retry = () => {
    setSwarm((s) => ({ ...s, loading: Boolean(statusUrl), error: null }));
    setAttempt((a) => a + 1);
  };

  // Show the chosen ADMIN subtitle track (and hide the others). Runs whenever
  // the CC selection or playback mode changes — src swaps reset text-track modes.
  //
  // Only PoKu's own <track> children are ours to drive. hls.js publishes the
  // manifest's SUBTITLES renditions as text tracks of its own, and sweeping
  // every track to "disabled" here is exactly why a hosted movie arrived with
  // its English subtitles (DEFAULT=YES in the manifest) switched off.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || hlsSubs.length) return undefined;
    const apply = () => {
      const list = v.textTracks;
      for (let i = 0; i < list.length; i += 1) {
        const tt = list[i];
        if (!subs.some((t) => t.label === tt.label)) continue;
        const isChosen = ccOn != null && tt.label === ccOn;
        tt.mode = isChosen ? "showing" : "disabled";
      }
    };
    apply();
    v.addEventListener("loadedmetadata", apply);
    return () => v.removeEventListener("loadedmetadata", apply);
  }, [ccOn, hlsSubs.length, subs, hls.active, streamUrl]);

  // Persist progress (throttled like the Vidking message handler).
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setTime(v.currentTime);
    const now = Date.now();
    const last = lastSavedRef.current;
    const urgent = v.paused || v.ended;
    if (!urgent && now - last.at < 5000 && Math.abs(v.currentTime - last.position) < 5) return;
    last.at = now;
    last.position = v.currentTime;
    saveProgress({
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      title: request.title,
      subtitle: request.subtitle,
      posterPath: request.posterPath,
      backdropPath: request.backdropPath,
      positionSeconds: v.ended ? v.duration : v.currentTime,
      durationSeconds: v.duration || 0,
      updatedAtMillis: now,
      completed: v.ended,
    });
  };

  useEffect(
    () => () => {
      clearTimeout(lastTapRef.current?.timer);
      clearTimeout(rippleTimerRef.current);
    },
    [],
  );

  const bufferedPct = dur > 0 ? Math.min(100, (time / dur) * 100) : 0;

  // Which of the two right-hand controls is worth showing. They have one job
  // each, the split YouTube uses: CC turns subtitles off/on, the gear picks the
  // audio track. So the CC toggle needs subtitle tracks to act on, and the gear
  // needs a genuine audio choice (in-manifest tracks for HLS, our own
  // renditions for hybrid). Nothing is shown when neither applies.
  const hasAudioChoice = (hls.active || hybridAudio) && hls.tracks.length > 1;
  // Either kind of subtitle counts: ours (admin-mapped) or the stream's own.
  const hasSubs = subs.length > 0 || hlsSubs.length > 0;
  const subsOn = hlsSubs.length > 0 ? hlsCc != null : ccOn != null;
  const hasSettings = hasAudioChoice;

  // CC is a plain toggle, YouTube-style: one press turns the current subtitle
  // off or back on, without opening the sheet. Switching on picks the same
  // track the initial load would have chosen (English, else the first), so the
  // button and the default never disagree.
  const toggleCc = () => {
    if (hlsSubs.length) {
      hlsCcTouchedRef.current = true;
      setHlsCc((cur) => {
        if (cur != null) return null;
        const pick =
          hlsSubs.find((t) => t.isDefault) ||
          hlsSubs.find((t) => /english|^en\b/i.test(`${t.name} ${t.code || ""}`)) ||
          hlsSubs[0];
        return pick.index;
      });
      return;
    }
    ccTouchedRef.current = true;
    setCcOn((cur) => {
      if (cur != null) return null;
      const english = subs.find((t) => /english|^en\b/i.test(`${t.label || ""} ${t.lang || ""}`));
      return (english || subs[0])?.label ?? null;
    });
  };

  return (
    <div
      ref={stageRef}
      className={`player-screen torrent-player${controls ? "" : " hide-cursor"}`}
      onClick={handleStageTap}
    >
      <video
        ref={videoRef}
        className="torrent-video"
        playsInline
        // AirPlay: WebKit only offers the video to an Apple TV when the element
        // is explicitly castable.
        x-webkit-airplay="allow"
        disablePictureInPicture
        // Muted autoplay start is what iOS allows without a fresh gesture;
        // the user taps once for sound (same policy as the Vidking embed).
        muted
        autoPlay
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onPlay={() => {
          setPlaying(true);
          setSwarm((s) => ({ ...s, loading: false, error: null }));
        }}
        onPause={() => setPlaying(false)}
        onPlaying={() => setSwarm((s) => ({ ...s, loading: false, error: null }))}
        onCanPlay={() => setSwarm((s) => ({ ...s, loading: false }))}
        onWaiting={() => setSwarm((s) => ({ ...s, loading: true }))}
        onVolumeChange={(e) => {
          setVolume(e.target.volume);
          setMuted(e.target.muted);
        }}
        onError={(e) => {
          if (!hls.active) {
            if (isHosted) {
              diagnoseMediaFailure(e.target.currentSrc || streamUrl, e.target.error?.code);
            }
            setSwarm((s) => ({
              ...s,
              loading: false,
              error: isHosted
                ? "The stream could not be opened. The host may have removed the file or it is temporarily down."
                : "The stream could not be opened. It may have no seeds right now.",
            }));
          }
        }}
      >
        {subs.map((t) => (
          <track
            key={t.id}
            kind="subtitles"
            label={t.label}
            srcLang={t.lang.slice(0, 3).toLowerCase()}
            src={t.url}
          />
        ))}
      </video>

      {/* Hybrid only: the audio rendition we own, synced to the host's video by
          the effect above. No controls — the bar drives it. */}
      {hybridAudio && <audio ref={audioRef} className="torrent-audio" playsInline preload="auto" />}

      {/* YouTube-style touch gestures: double-tap left/right seeks ∓10s
          (ripple feedback), double-tap center fullscreens, single tap toggles
          the controls. Purely visual — the bar below it does the precision
          work, so it sits at z-index 4 (bar is 6, overlays/back are 7). */}
      <div className="torrent-gesture-layer" aria-hidden="true">
        {ripple?.side === "left" && (
          <div className="poku-ripple show" style={{ left: "0%" }}>
            <svg viewBox="0 0 24 24">
              <path d={Icon.replay} />
            </svg>
            <span>{ripple.label}</span>
          </div>
        )}
        {ripple?.side === "right" && (
          <div className="poku-ripple show" style={{ right: "0%" }}>
            <svg viewBox="0 0 24 24">
              <path d={Icon.forward} />
            </svg>
            <span>{ripple.label}</span>
          </div>
        )}
      </div>

      {/* Buffering veil — visual only, never blocks the controls below it. */}
      {swarm.loading && !swarm.error && (
        <div className="player-overlay soft">
          <div className="spinner" />
          <p className="status-label">
            {statusUrl
              ? swarm.peers === -1
                ? "Streaming from cloud cache…"
                : swarm.peers > 0
                  ? `Connecting to swarm… ${swarm.peers} peers`
                  : "Preparing cloud stream…"
              : "Loading stream…"}
          </p>
          {swarm.progress > 0 && swarm.progress < 1 && (
            <p className="status-label muted">
              {swarm.peers > 0
                ? `${(swarm.progress * 100).toFixed(1)}% · ${(swarm.speed / 1_000_000).toFixed(2)} MB/s`
                : `Cloud downloading… ${(swarm.progress * 100).toFixed(0)}%`}
            </p>
          )}
        </div>
      )}
      {swarm.error && (
        <div className="player-overlay">
          <h2 className="status-title">Stream unavailable</h2>
          <p className="status-label">{swarm.error}</p>
          <div className="player-actions">
            <button className="btn btn-ghost" onClick={retry}>
              TRY AGAIN
            </button>
            {onBack && (
              <button className="btn btn-ghost" onClick={onBack}>
                BACK
              </button>
            )}
          </div>
        </div>
      )}

      {controls && onBack && (
        <button className="player-back" onClick={onBack} aria-label="Back">
          ←
        </button>
      )}
      {controls && request.title && <span className="torrent-title">{request.title}</span>}

      {/* PoKu control bar — two rows: current-time + seek + duration on top,
          transport icons below. Same layout and icon language as the YouTube
          player and the injected embed bar, so every movie shows identical
          uncongested controls on every screen size. */}
      <div className={`torrent-ui${controls ? "" : " hidden"}`}>
        {/* Audio only — the sheet behind the gear, marking the current track.
            Subtitles are the CC button's job, so nothing about them appears
            here. It lives INSIDE the bar so it always sits directly above it,
            whatever the bar's height on this screen. */}
        {menuOpen && (
          <div className="torrent-lang-panel" onClick={(e) => e.stopPropagation()}>
            <div className="torrent-lang-head">
              <span>Settings</span>
              <button className="torrent-lang-close" onClick={() => setMenuOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="torrent-lang-scroll">
              {/* Audio only — the gear's job is picking the track. In-manifest
                  tracks for HLS, our own renditions for hybrid (where the host's
                  player would only ever have one). Subtitles live on the CC
                  button instead, so there is no language list in here. */}
              {hasAudioChoice && (hlsRef.current || hybridAudio) && (
                <>
                  <div className="torrent-lang-group">Audio</div>
                  {hls.tracks.map((t, i) => {
                    const on = (hlsRef.current?.audioTrack ?? hls.current) === i;
                    // The film's own language, tagged — so why it plays by
                    // default answers itself.
                    const original = isOriginalLanguage(t, request.originalLanguage);
                    return (
                      <button
                        key={`${t.index ?? i}-${t.code || t.name || t.label}`}
                        className={`torrent-lang-row${on ? " active" : ""}`}
                        onClick={() => switchLanguage(i)}
                      >
                        <span className="torrent-lang-name">
                          {languageLabel(t.code, t.name || t.label)}
                          {original && <span className="torrent-lang-orig">Original</span>}
                        </span>
                        {on && <span className="tick">✓</span>}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
        <div className="torrent-seek-row" onClick={(e) => e.stopPropagation()}>
          <span className="torrent-time">{fmtTime(time)}</span>
          <div className="torrent-track">
            <div className="torrent-buffered" style={{ width: `${bufferedPct}%` }} />
            <input
              type="range"
              className="torrent-seek"
              min={0}
              max={dur > 0 ? Math.floor(dur) : 0}
              step={1}
              value={dur > 0 ? Math.floor(Math.min(time, dur)) : 0}
              disabled={dur <= 0}
              onChange={(e) => {
                const v = videoRef.current;
                if (!v) return;
                const next = Number(e.target.value);
                v.currentTime = next;
                setTime(next);
                setActiveAt(Date.now());
              }}
              aria-label="Seek"
            />
          </div>
          <span className="torrent-time">{fmtTime(dur)}</span>
        </div>
        <div className="torrent-row" onClick={(e) => e.stopPropagation()}>
          <div className="torrent-group">
            <BarButton label={playing ? "Pause" : "Play"} onClick={togglePlay} className="torrent-btn-play">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={playing ? Icon.pause : Icon.play} />
              </svg>
            </BarButton>
            <BarButton label="Back 10 seconds" onClick={() => skip(-10)} className="with-tag">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={Icon.replay} />
              </svg>
              <span className="btn-tag">10</span>
            </BarButton>
            <BarButton label="Forward 10 seconds" onClick={() => skip(10)} className="with-tag">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={Icon.forward} />
              </svg>
              <span className="btn-tag">10</span>
            </BarButton>
            <BarButton label={muted ? "Unmute" : "Mute"} onClick={toggleMute}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={muted || volume === 0 ? Icon.volMute : Icon.volUp} />
              </svg>
            </BarButton>
            <div className="torrent-vol">
              <input
                type="range"
                className="torrent-seek"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const v = videoRef.current;
                  if (!v) return;
                  const next = Number(e.target.value);
                  if (!hybridAudio) {
                    v.volume = next;
                    v.muted = next === 0;
                  }
                  setVolume(next);
                  setMuted(next === 0);
                  setActiveAt(Date.now());
                }}
                aria-label="Volume"
              />
            </div>
            <span className="torrent-vol-pct">{muted ? 0 : Math.round(volume * 100)}%</span>
          </div>
          <div className="torrent-group">
            {/* Two separate controls, the pair YouTube uses: CC toggles the
                subtitle straight away, the gear opens the audio sheet. Both sit
                with fullscreen at the end of the bar, never over the picture,
                and each appears only when it leads somewhere. */}
            {hasSubs && (
              <BarButton
                label={subsOn ? "Turn subtitles off" : "Turn subtitles on"}
                onClick={toggleCc}
                className={subsOn ? "on" : ""}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={Icon.subs} />
                </svg>
              </BarButton>
            )}
            {hasSettings && (
              <BarButton
                label="Settings"
                onClick={() => {
                  setActiveAt(Date.now());
                  setMenuOpen((v) => !v);
                }}
                className={menuOpen ? "on" : ""}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={Icon.settings} />
                </svg>
              </BarButton>
            )}
            {/* Cast to a TV. Rendered only when the browser has a picker and
                the source is one a receiver can fetch itself (see cast.js);
                "Casting" in gold means the TV owns playback and the bar now
                just controls it. */}
            {cast.route && cast.castable && (
              <BarButton
                label={
                  cast.connected
                    ? "Casting to a TV"
                    : cast.available
                      ? "Cast to a TV"
                      : "Cast — no devices found"
                }
                onClick={toggleCast}
                className={cast.connected ? "on" : ""}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={Icon.cast} />
                </svg>
              </BarButton>
            )}
            <BarButton label={fullscreen ? "Exit fullscreen" : "Fullscreen"} onClick={toggleFullscreen}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={fullscreen ? Icon.fsExit : Icon.fsEnter} />
              </svg>
            </BarButton>
          </div>
        </div>
      </div>
    </div>
  );
}
